import { Box, Link, Typography } from '@mui/material'
import { AttachMoney, DateRange } from '@mui/icons-material'
import { FormatCurrency } from 'core/Utilities'
import { type DocumentSummary } from './types'
import { colors } from './styles'

// Same extension labels/colors as Components/DocumentList/DocumentInfo
function fileExtension (fileName: string): string {
  const parts = fileName.toUpperCase().split('.')
  const ext = parts.length > 1 ? parts[parts.length - 1] : ''
  return ext === 'JPEG' || ext === 'HEIC' ? 'JPG' : ext
}

function extensionColor (ext: string): string {
  if (ext === 'PDF') return colors.purple
  if (ext === 'JPG') return colors.red
  return colors.teal
}

export function FileBadge (props: { fileName: string }): JSX.Element {
  const ext = fileExtension(props.fileName)
  return <Box sx={{
    width: 40,
    height: 40,
    flexShrink: 0,
    borderRadius: '4px',
    bgcolor: extensionColor(ext),
    color: 'white',
    fontWeight: 800,
    fontSize: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }}
  >
    {ext.slice(0, 4)}
  </Box>
}

export function FileNameLink (props: { fileName: string, onClick?: () => void }): JSX.Element {
  const sx = { color: colors.purple, fontSize: 14, lineHeight: 1.3, textDecorationColor: colors.purple, textAlign: 'left', wordBreak: 'break-word' } as const
  return props.onClick != null
    ? <Link component='button' onClick={props.onClick} sx={sx} data-testid='documentFileName'>{props.fileName}</Link>
    : <Typography sx={{ ...sx, textDecoration: 'underline', fontWeight: 600 }} data-testid='pendingFileName'>{props.fileName}</Typography>
}

interface DocumentCardProps {
  document: DocumentSummary
  onOpen?: (documentID: number) => void
}

export function DocumentCard (props: DocumentCardProps): JSX.Element {
  const { document } = props
  const openFile = (): void => {
    if (props.onOpen != null) props.onOpen(document.documentID)
    else window.open(`${process.env.REACT_APP_SERVER_URL ?? ''}/Document/OpenFile/${document.documentID}`, '_blank')
  }

  return <Box
    data-testid={`document-${document.documentID}`}
    sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px', px: '13px', py: '18px', border: `1px solid ${colors.teal}`, borderRadius: '12px', bgcolor: colors.tealLight }}
  >
    <Box sx={{ display: 'flex', gap: '10px', width: '100%' }}>
      <FileBadge fileName={document.fileName} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
        <FileNameLink fileName={document.fileName} onClick={openFile} />
        {document.appliesTo.length > 0 && <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px 8px', px: '7px', py: '3px' }}>
          {document.appliesTo.map(person =>
            <Box
              key={`${person.isChild ? 'child' : 'caregiver'}-${person.id}`}
              sx={{ bgcolor: colors.chip, color: 'white', borderRadius: '10px', px: '8px', py: '2px', fontSize: 10, lineHeight: 1.3, whiteSpace: 'nowrap' }}
            >
              {person.name}
            </Box>
          )}
        </Box>}
      </Box>
    </Box>
    {(document.incomeAmount !== null || document.incomeYear !== null) && <Box sx={{ display: 'flex', gap: '10px' }}>
      {document.incomeAmount !== null && <Typography sx={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: 12, color: '#0F0F10' }}>
        <AttachMoney sx={{ fontSize: 16 }} />{FormatCurrency(document.incomeAmount)}
      </Typography>}
      {document.incomeYear !== null && <Typography sx={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: 12, color: '#0F0F10' }}>
        <DateRange sx={{ fontSize: 16 }} />{document.incomeYear}
      </Typography>}
    </Box>}
  </Box>
}
